import DESIGN_TOKEN_SURFACES from './design-token-surfaces.mjs'

/* §6 Tenant accent system — OKLCH derivation per DESIGN.md */

function hexToRgb(hex: string): [number, number, number] {
  return [
    parseInt(hex.slice(1, 3), 16) / 255,
    parseInt(hex.slice(3, 5), 16) / 255,
    parseInt(hex.slice(5, 7), 16) / 255,
  ]
}

function toLinear(c: number): number {
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4
}

function relativeLuminance(r: number, g: number, b: number): number {
  return 0.2126 * toLinear(r) + 0.7152 * toLinear(g) + 0.0722 * toLinear(b)
}

export function contrastRatio(hex1: string, hex2: string): number {
  const [r1, g1, b1] = hexToRgb(hex1)
  const [r2, g2, b2] = hexToRgb(hex2)
  const l1 = relativeLuminance(r1, g1, b1)
  const l2 = relativeLuminance(r2, g2, b2)
  return (Math.max(l1, l2) + 0.05) / (Math.min(l1, l2) + 0.05)
}

/** Exported with `oklchToHex` so the derivations below can be driven directly
 *  in tests against hostile inputs; both are pure and deterministic. */
export function hexToOklch(hex: string): [number, number, number] {
  const [r, g, b] = hexToRgb(hex)
  const rl = toLinear(r),
    gl = toLinear(g),
    bl = toLinear(b)

  const lms_l = 0.4122214708 * rl + 0.5363325363 * gl + 0.0514459929 * bl
  const lms_m = 0.2119034982 * rl + 0.6806995451 * gl + 0.1073969566 * bl
  const lms_s = 0.0883024619 * rl + 0.2817188376 * gl + 0.6299787005 * bl

  const l_ = Math.cbrt(lms_l),
    m_ = Math.cbrt(lms_m),
    s_ = Math.cbrt(lms_s)

  const L = 0.2104542553 * l_ + 0.793617785 * m_ - 0.0040720468 * s_
  const a = 1.9779984951 * l_ - 2.428592205 * m_ + 0.4505937099 * s_
  const bv = 0.0259040371 * l_ + 0.7827717662 * m_ - 0.808675766 * s_

  const C = Math.sqrt(a * a + bv * bv)
  const H = (Math.atan2(bv, a) * 180) / Math.PI
  return [L, C, H < 0 ? H + 360 : H]
}

export function oklchToHex(L: number, C: number, H: number): string {
  const hRad = (H * Math.PI) / 180
  const a = C * Math.cos(hRad)
  const bv = C * Math.sin(hRad)

  const l_ = L + 0.3963377774 * a + 0.2158037573 * bv
  const m_ = L - 0.1055613458 * a - 0.0638541728 * bv
  const s_ = L - 0.0894841775 * a - 1.291485548 * bv

  const lms_l = l_ ** 3,
    lms_m = m_ ** 3,
    lms_s = s_ ** 3

  const rl = 4.0767416621 * lms_l - 3.3077115913 * lms_m + 0.2309699292 * lms_s
  const gl = -1.2684380046 * lms_l + 2.6097574011 * lms_m - 0.3413193965 * lms_s
  const bl = -0.0041960863 * lms_l - 0.7034186147 * lms_m + 1.707614701 * lms_s

  const toGamma = (c: number) => {
    const v = Math.max(0, Math.min(1, c))
    return v <= 0.0031308 ? 12.92 * v : 1.055 * v ** (1 / 2.4) - 0.055
  }
  const byte = (c: number) =>
    Math.max(0, Math.min(255, Math.round(toGamma(c) * 255)))
      .toString(16)
      .padStart(2, '0')

  return `#${byte(rl)}${byte(gl)}${byte(bl)}`
}

function linearToHsl(
  r: number,
  g: number,
  b: number
): [number, number, number] {
  const max = Math.max(r, g, b),
    min = Math.min(r, g, b)
  const l = (max + min) / 2
  if (max === min) return [0, 0, Math.round(l * 100)]
  const d = max - min
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min)
  let h: number
  switch (max) {
    case r:
      h = ((g - b) / d + (g < b ? 6 : 0)) / 6
      break
    case g:
      h = ((b - r) / d + 2) / 6
      break
    default:
      h = ((r - g) / d + 4) / 6
  }
  return [Math.round(h * 360), Math.round(s * 100), Math.round(l * 100)]
}

export type AccentRamp = {
  accent: string
  accentFg: string
  accentText: string
  accentHover: string
  accentPressed: string
  accentMuted: string
  focusRing: string
  /**
   * The primary button's edge — a hex, or the literal `'transparent'`.
   *
   * §7 requires ≥3:1 on interactive component boundaries; §9 Folio specifies a
   * borderless primary. Several graded swap accents fail that floor in at least
   * one mode — at #FFFFE0 the CTA is not visibly a button. The two rules cannot
   * both be unconditional, so §6/§9 say Folio has NO DECORATIVE keyline and an
   * accessible boundary appears only when the raw brand fill cannot draw the
   * edge itself. Exact per-surface ratios are deliberately not quoted here:
   * they are outputs that move whenever a surface token is retuned, and the
   * floors are enforced by the deterministic sweep in theme.test.ts.
   *
   * `--accent-pressed` was proposed for this and REJECTED: it is a state shade,
   * never validated as a boundary, and it stays under 3:1 for pale accents in
   * light mode and dark accents in dark mode (pinned in theme.test.ts).
   */
  accentBoundary: string
}

/** §7's interactive-boundary floor. */
export const BOUNDARY_MIN_CONTRAST = 3
/** A boundary indistinguishable from its own fill is not a boundary. */
export const BOUNDARY_MIN_FILL_SEPARATION = 2
/**
 * The focus indicator's NORMATIVE floor: the painted ring against the surface
 * actually adjacent to it.
 *
 * `button-variants.ts` sets `focus-visible:ring-2 ring-offset-2` with
 * `ring-offset-background`, so what physically touches the ring is the
 * ring-offset colour — NOT the button fill and NOT the boundary. A ring is
 * visible when it separates from what it abuts, which is why this is the number
 * §7 is graded on.
 *
 * An earlier revision of this file screened the boundary against `--focus-ring`
 * at 2:1 and called that focus visibility. It is not: a 2px offset band sits
 * between rim and ring, so those two colours are never adjacent, and the
 * comparison proves nothing about whether focus is perceivable. It also cost
 * real brand fidelity, pushing derived edges off the tenant's hue and some of
 * them to neutral ink, to buy a property that was already satisfied everywhere.
 * (The figures that quantified that cost were removed along with the code that
 * produced them: this file may not carry numbers nothing committed can
 * reproduce — the same rule the slice applied to DESIGN.md §6.) The screen is
 * gone; this is what replaced it.
 */
export const FOCUS_RING_MIN_CONTRAST = 3

/**
 * What the focus ring abuts on its INNER edge: `ring-offset-background`, i.e.
 * the shadcn `--background` token (src/styles/globals.css `--background`,
 * `36 33% 97%` light / `40 9% 7%` dark).
 *
 * NOTE this is only ONE of the ring's two neighbours. Painted order outward is
 * `[border][ring-offset][ring][page surface]`, so the ring also abuts whatever
 * surface the control sits on. `deriveFocusRing` is therefore graded against
 * `BUTTON_SURFACES` (which contains this value), not against this alone — see
 * the note on that function.
 */
export const RING_OFFSET_SURFACES = {
  light: [DESIGN_TOKEN_SURFACES.ringOffset.light],
  dark: [DESIGN_TOKEN_SURFACES.ringOffset.dark],
} as const

/**
 * Surfaces per DESIGN DIRECTION, named, from the §8 and §9 token tables.
 *
 * These were previously collapsed to a single list holding ONLY the Folio
 * values, while DESIGN.md maintains two live directions with materially
 * different surfaces (Ledger dark `#0B0D11/#12151A/#181C23` is a full step
 * darker than Folio's). Every boundary contrast figure the design system has
 * published was therefore computed against one direction. Naming both, and
 * deriving the validation set as their union, makes the derivation correct
 * whichever direction ships and makes a future third direction impossible to
 * add silently.
 *
 * Tokens, by name, per direction: `--bg`, `--surface`, `--surface-2`. `--card`
 * and `--popover` collapse onto those values today; `--background` is a shadcn
 * token independent of direction and is contributed separately below.
 */
export const DIRECTION_SURFACES = DESIGN_TOKEN_SURFACES.directions

/**
 * Direction-independent shadcn surfaces a primary button can also sit on.
 * `--background` is what Sheet and Dialog paint, and in DARK it is not equal to
 * `--bg` (`#131210` vs `#1C1A17`); in LIGHT the two collapse onto `#FAF8F5`.
 *
 * ALIASED, not re-declared. This is the same `--background` token as
 * `RING_OFFSET_SURFACES` — Tailwind's `ring-offset-background` is bound to it —
 * so holding two independent copies meant a retune could update one and leave
 * the other silently stale, in code AND in the test that reads it. One value,
 * two names, so they cannot drift apart.
 */
export const SHADCN_SURFACES = RING_OFFSET_SURFACES

const dedupe = (values: readonly string[]) =>
  [...new Set(values.map((v) => v.toUpperCase()))].sort()

/**
 * Terminator candidates for the boundary walk, in fixed preference order.
 *
 * The mode's own ink comes first so the previous behaviour is preserved wherever
 * it was valid; the opposite ink and then absolute black/white follow, widening
 * the range of surface matrices that CAN be cleared. Order is fixed, so the
 * resolution is deterministic for a given input.
 *
 * This comment previously ended "...so the resolution is TOTAL — there is always
 * something to return that clears §7, whatever the surface matrix becomes." That
 * is FALSE and is withdrawn; see the counterexample in `resolveTerminator` below
 * and its test `E6`. Extending the list does not make the resolution total, and
 * the same false claim was carried in DESIGN.md §6 rule 4, corrected there too.
 */
export const TERMINATOR_CANDIDATES = {
  light: ['#1D1A15', '#F2EFE8', '#000000', '#FFFFFF'],
  dark: ['#F2EFE8', '#1D1A15', '#FFFFFF', '#000000'],
} as const

/**
 * Every surface a primary button is allowed to sit on, in EITHER direction.
 *
 * Computed as the union rather than typed out, so it cannot drift from the
 * direction matrices it is supposed to summarise — the previous hand-maintained
 * list is exactly how the Ledger surfaces went missing without anyone noticing.
 * Validating against the union is deliberately conservative: a boundary derived
 * here is correct whichever direction is live, and switching direction can
 * never invalidate an already-derived edge.
 */
export const BUTTON_SURFACES = {
  light: dedupe([
    ...DIRECTION_SURFACES.ledger.light,
    ...DIRECTION_SURFACES.folio.light,
    ...SHADCN_SURFACES.light,
  ]),
  dark: dedupe([
    ...DIRECTION_SURFACES.ledger.dark,
    ...DIRECTION_SURFACES.folio.dark,
    ...SHADCN_SURFACES.dark,
  ]),
} as const

// The app surface the accent text is read against, per mode. Light `--surface`
// is #FFFFFF; dark `--surface` is #242220 (src/styles/globals.css). `--bg` is
// near-identical to `--surface` today, so validating against `--surface` covers
// accent text on the page background too; if the two ever diverge materially,
// add `--bg` as a third contrast reference here.
const SURFACE_HEX = { light: '#FFFFFF', dark: '#242220' } as const

/**
 * Accessible brand fidelity: the raw brand hex stays the fill/swatch color, but
 * accent-AS-TEXT (links, active nav/tab labels, rails) needs its own token that
 * is legible on the surface. Keep the brand hue and a capped chroma, then walk
 * lightness (deterministically, toward the surface's opposite end) until the
 * result clears 4.5:1 against BOTH the base surface and the accent-muted wash it
 * can sit on. Falls back to the guaranteed-legible ink token — never a failure
 * state. Mirrors DESIGN.md §6's "accent-as-link vs surface ≥ 4.5:1" as a real,
 * derived token rather than reusing the unvalidated raw accent.
 */
function deriveAccentText(
  brandL: number,
  chroma: number,
  hue: number,
  isDark: boolean,
  accentMutedHex: string
): string {
  const surfaceHex = isDark ? SURFACE_HEX.dark : SURFACE_HEX.light
  const cappedC = Math.min(chroma, 0.16)
  const step = isDark ? 0.02 : -0.02
  let L = brandL
  for (let i = 0; i < 60; i++) {
    const candidate = oklchToHex(L, cappedC, hue)
    if (
      contrastRatio(candidate, surfaceHex) >= 4.5 &&
      contrastRatio(candidate, accentMutedHex) >= 4.5
    ) {
      return candidate
    }
    L += step
    if (L <= 0 || L >= 1) break
  }
  return isDark ? '#F2EFE8' : '#1D1A15'
}

/* §6 clamped default — the Ledger cool blue. Swap for tenants.branding.accent_hex when tenant system ships. */
export const DEFAULT_ACCENT =
  (import.meta.env.VITE_ACCENT_HEX as string | undefined) ?? '#2D70F5'

/**
 * `--focus-ring`, with §6's stated floor actually ENFORCED.
 *
 * §6 rule 9 has always said "focus-ring vs surface >= 3:1". Nothing checked it:
 * the ring was a bare `oklchToHex(0.55 | 0.72, C*0.7, H)` and whatever came out
 * was shipped. Across the full accepted input domain the fixed lightness does
 * satisfy the floor everywhere, so this guard changes NO value today and is
 * verified not to by pinned outputs in theme.test.ts.
 *
 * It is still worth having as code rather than as a comforting measurement: the
 * floor is a REQUIREMENT, and a requirement that holds only because of the
 * current value of an unrelated token is one surface retune away from being
 * false. The walk moves lightness away from the surface end, exactly as the
 * accent-text and boundary walks do, and terminates in ink.
 *
 * GRADED AGAINST BOTH NEIGHBOURS. A first cut of this function screened only
 * `RING_OFFSET_SURFACES` — the ring's inner neighbour. But the painted order is
 * `[border][ring-offset][ring][page surface]`, so the ring abuts the page
 * surface too, and the page surface is the WORSE case: the ring-offset value
 * leaves ~4.16 light / ~6.72 dark of headroom while the true worst adjacent
 * surface (`--surface-2`, `#F3F0EA` light / `#2C2A25` dark) leaves ~3.87 /
 * ~5.15. `theme.test.ts` B4 already asserted the wider property, so the code
 * enforced less than its own test demanded — precisely the "true only because
 * of an unrelated token's current value" defect this function exists to remove.
 * The default is now the full union, matching B4. Verified to change no output.
 *
 * `surfaces` is a parameter so the resolution can be exercised against a
 * hostile matrix in tests; production always uses the default.
 */
export function deriveFocusRing(
  C: number,
  H: number,
  isDark: boolean,
  surfaces: readonly string[] = BUTTON_SURFACES[isDark ? 'dark' : 'light']
): string {
  const clears = (candidate: string) =>
    surfaces.every(
      (s) => contrastRatio(candidate, s) >= FOCUS_RING_MIN_CONTRAST
    )

  const baseL = isDark ? 0.72 : 0.55
  const chroma = C * 0.7
  const base = oklchToHex(baseL, chroma, H)
  if (clears(base)) return base

  const step = isDark ? 0.02 : -0.02
  for (let i = 1; i <= 50; i += 1) {
    const candidateL = Math.min(1, Math.max(0, baseL + step * i))
    const candidate = oklchToHex(candidateL, chroma, H)
    if (clears(candidate)) return candidate
    if (candidateL === 0 || candidateL === 1) break
  }

  // Terminator, resolved exactly as the boundary walk's is.
  //
  // This was a bare `return isDark ? '#F2EFE8' : '#1D1A15'` — the mode's ink,
  // unconditionally, with nothing checking it cleared the very floor this
  // function exists to enforce. That is the identical defect the slice removed
  // from `deriveAccentBoundary`, left in place one function away: driven against
  // a surface matrix that defeats the walk, it returned a ring at 2.18:1. Not
  // reachable with today's surfaces, which is precisely why it needed fixing
  // rather than excusing — the walk is unreachable today too.
  //
  // No fill constraint: the focus ring has no fill to separate from, so the
  // first candidate clearing the floor wins.
  return resolveTerminator(
    TERMINATOR_CANDIDATES[isDark ? 'dark' : 'light'],
    surfaces,
    [],
    FOCUS_RING_MIN_CONTRAST
  )
}

/**
 * The boundary walk's terminator, resolved deterministically and fail-closed.
 *
 * This used to be inlined in `deriveAccentBoundary`, and before that it returned
 * the mode's ink unconditionally with a comment claiming the required predicates
 * held "by construction". That claim was prose, not a guarantee: nothing checked
 * it, so a future retune of a surface token could have made the terminator
 * itself fail §7 silently.
 *
 * Candidates are tried in a FIXED order and the first that clears every allowed
 * surface wins — that is the §7 REQUIREMENT. Fill separation is a craft floor
 * and only breaks ties among candidates that already clear §7. Ordering the
 * mode's own ink first preserves the previous output wherever it was valid.
 *
 * WHAT THIS IS AND IS NOT GUARANTEED TO DO. The function is TOTAL in the weak
 * sense only: it always returns a colour. It is NOT guaranteed to return a
 * §7-COMPLIANT colour, and an earlier comment here claimed otherwise ("total for
 * ANY surface matrix"). That claim is false and is withdrawn.
 *
 * Counterexample, verified: with surfaces `#555555` and `#BBBBBB`, every listed
 * candidate fails 3:1 against at least one of them — light ink 2.3264 vs
 * `#555555`, `#F2EFE8` 1.6718 vs `#BBBBBB`, black 2.8168 vs `#555555`, white
 * 1.9198 vs `#BBBBBB`. A mid-grey surface pair defeats an ink-and-extremes
 * candidate list, because no single colour can be far from both mid-greys.
 *
 * So the final reduce is a genuine BEST-EFFORT fallback, not a proof: it returns
 * the candidate with the best worst-case surface contrast, which may be below
 * the floor. It exists so the function degrades predictably rather than
 * returning something arbitrary or throwing.
 *
 * What IS guaranteed, and is asserted in theme.test.ts against the real matrix,
 * is that TODAY'S `BUTTON_SURFACES` are cleared by at least one candidate. That
 * is an invariant about the current tokens, and it is tested as such — the honest
 * claim, rather than a mathematical totality the candidate list cannot deliver.
 *
 * EXPORTED DELIBERATELY. While this logic was inlined, the only test covering it
 * declared its own local copy of the resolver and asserted against that, so the
 * production branch had no executable coverage at all: a mutant returning a flat
 * `#B0B0B0` — 1.9067:1 against the light surfaces, a live §7 violation — passed
 * the entire suite. A test that re-implements the code it is verifying proves
 * only that the test is self-consistent. It is a parameterised pure function so
 * the real branch can be driven with a hostile matrix.
 */
export function resolveTerminator(
  candidates: readonly string[],
  surfaces: readonly string[],
  fills: readonly string[],
  /** The floor the candidate must clear. Passed explicitly rather than read from
   *  a module constant, because two callers govern two different §-floors and a
   *  shared hardcoded constant would silently apply one caller's floor to the
   *  other if either were ever retuned. */
  minContrast: number = BOUNDARY_MIN_CONTRAST
): string {
  const clearsSurfaces = (c: string) =>
    surfaces.every((s) => contrastRatio(c, s) >= minContrast)
  const separableFromFill = (c: string) =>
    fills.every((f) => contrastRatio(c, f) >= BOUNDARY_MIN_FILL_SEPARATION)

  const eligible = candidates.filter(clearsSurfaces)
  const preferred = eligible.filter(separableFromFill)
  if (preferred.length > 0) return preferred[0]
  if (eligible.length > 0) return eligible[0]

  // Nothing clears the floor — pick the candidate with the best worst-case
  // surface contrast rather than returning something arbitrary.
  return candidates.reduce((best, c) => {
    const worst = (x: string) =>
      Math.min(...surfaces.map((s) => contrastRatio(x, s)))
    return worst(c) > worst(best) ? c : best
  })
}

/**
 * The primary button's edge, derived deterministically.
 *
 * Order is fixed so the same brand always yields the same boundary:
 *  1. no boundary at all when the RAW FILL already clears the floor on every
 *     surface it may sit on — Folio's borderless primary is preserved wherever
 *     accessibility does not require otherwise;
 *  2. a walk in the brand hue away from the surface end, keeping the hue and a
 *     capped chroma — the same technique `deriveAccentText` uses, so the edge
 *     reads as the tenant's colour rather than as generic chrome;
 *  3. high-contrast ink as the terminator.
 *
 * A brand-hue FOCUS-RING candidate was tried here and removed: --focus-ring is
 * a state shade, exactly what disqualified --accent-pressed, and it took the
 * boundary role in the large majority of cases — including every graded accent
 * — so a button rested wearing its own focus colour. The walk already yields a
 * brand-hue edge.
 *
 * A candidate must clear 3:1 against EVERY allowed surface and be separable
 * from the fill it outlines; the walk is also checked against the hover and
 * pressed shades, because an edge that vanishes on hover disappears exactly
 * when the control is being aimed at.
 *
 * A revision of this function additionally screened candidates against
 * `--focus-ring` at 2:1, on the theory that a rim close to the ring would make
 * focus unreadable. That was wrong twice over and has been REMOVED, not
 * merely disabled: rim and ring are separated by the 2px `ring-offset-2` band
 * so they are never adjacent, and the ring's real neighbours already clear 3:1
 * for every accepted input. The screen bought nothing and cost brand fidelity,
 * pushing derived rims away from the tenant's hue and some of them to neutral
 * ink. (It was measured before removal; the figures are not quoted here because
 * the code that produced them was deleted with the screen, and this file may not
 * carry numbers nothing committed can reproduce.)
 * `FOCUS_RING_MIN_CONTRAST` above is the property that actually governs focus.
 */
function deriveAccentBoundary(
  accentHex: string,
  hoverHex: string,
  pressedHex: string,
  L: number,
  C: number,
  H: number,
  isDark: boolean
): string {
  const surfaces = BUTTON_SURFACES[isDark ? 'dark' : 'light']
  const clearsSurfaces = (candidate: string) =>
    surfaces.every((s) => contrastRatio(candidate, s) >= BOUNDARY_MIN_CONTRAST)
  const separableFromFill = (candidate: string) =>
    [accentHex, hoverHex, pressedHex].every(
      (fill) => contrastRatio(candidate, fill) >= BOUNDARY_MIN_FILL_SEPARATION
    )

  // 1 — the fill draws its own edge in every state; no keyline needed.
  if (
    clearsSurfaces(accentHex) &&
    clearsSurfaces(hoverHex) &&
    clearsSurfaces(pressedHex)
  ) {
    return 'transparent'
  }

  // 2 — deterministic walk in the brand hue, away from the surface's end.
  const step = isDark ? 0.02 : -0.02
  const chroma = Math.min(C, 0.16)
  for (let i = 1; i <= 50; i += 1) {
    const candidateL = Math.min(1, Math.max(0, L + step * i))
    const candidate = oklchToHex(candidateL, chroma, H)
    if (clearsSurfaces(candidate) && separableFromFill(candidate)) {
      return candidate
    }
    if (candidateL === 0 || candidateL === 1) break
  }

  // 3 — terminator, resolved deterministically and fail-closed.
  return resolveTerminator(
    TERMINATOR_CANDIDATES[isDark ? 'dark' : 'light'],
    surfaces,
    [accentHex, hoverHex, pressedHex]
  )
}

export function deriveAccentRamp(hex: string, isDark: boolean): AccentRamp {
  // Preserve the tenant's EXACT brand color. White-label fidelity wins for the
  // base accent; readability is handled by choosing a contrasting foreground,
  // not by altering the brand color. Only the derived state shades and the
  // fg/muted/ring tokens are computed.
  const [L, C, H] = hexToOklch(hex)
  const accentHex = hex

  // accent-fg: pick whichever of white / ink / deep-ink reads best on the
  // client's actual accent. Accessibility lives here now — the text ON the
  // button, never the button color itself.
  const inkHex = isDark ? '#F2EFE8' : '#1D1A15'
  const darkInk = '#0A0806'
  const fgCandidates = ['#FFFFFF', inkHex, darkInk]
  const accentFg = fgCandidates.reduce((best, c) =>
    contrastRatio(accentHex, c) > contrastRatio(accentHex, best) ? c : best
  )

  // State shades — derived from the client's own L, nudged for hover/pressed.
  const hoverHex = oklchToHex(
    isDark ? Math.min(1, L + 0.04) : Math.max(0, L - 0.04),
    C,
    H
  )
  const pressedHex = oklchToHex(
    isDark ? Math.min(1, L + 0.07) : Math.max(0, L - 0.07),
    C,
    H
  )
  const accentMutedHex = oklchToHex(isDark ? 0.24 : 0.95, C * 0.25, H)
  const focusRingHex = deriveFocusRing(C, H, isDark)

  // accent-as-text: the exact brand hue, made legible on the surface. Never the
  // raw accent used directly as text on an unvalidated background.
  const accentTextHex = deriveAccentText(L, C, H, isDark, accentMutedHex)

  return {
    accent: accentHex,
    accentFg,
    accentText: accentTextHex,
    accentHover: hoverHex,
    accentPressed: pressedHex,
    accentMuted: accentMutedHex,
    focusRing: focusRingHex,
    accentBoundary: deriveAccentBoundary(
      accentHex,
      hoverHex,
      pressedHex,
      L,
      C,
      H,
      isDark
    ),
  }
}
export type FontPairingKey = 'folio' | 'ledger'

export interface FontPairing {
  key: FontPairingKey
  label: string
  uiFont: string
  monoFont: string
  description: string
}

export const FONT_PAIRINGS: FontPairing[] = [
  {
    key: 'folio',
    label: 'Folio',
    uiFont: 'Instrument Sans Variable',
    monoFont: 'Fragment Mono',
    description: 'Instrument Sans · Fragment Mono — warm, editorial',
  },
  {
    key: 'ledger',
    label: 'Ledger',
    uiFont: 'IBM Plex Sans',
    monoFont: 'IBM Plex Mono',
    description: 'IBM Plex Sans · IBM Plex Mono — cool, precise',
  },
]

export const DEFAULT_FONT_PAIRING: FontPairingKey = 'folio'

export function applyFontPairing(key: string): void {
  const pairing = FONT_PAIRINGS.find((p) => p.key === key) ?? FONT_PAIRINGS[0]
  const root = document.documentElement
  root.style.setProperty('--font-ui', `'${pairing.uiFont}'`)
  root.style.setProperty('--font-mono', `'${pairing.monoFont}'`)
}

export function applyAccentTokens(hex: string, isDark: boolean): void {
  const ramp = deriveAccentRamp(hex, isDark)

  const root = document.documentElement
  root.style.setProperty('--accent-color', ramp.accent)
  root.style.setProperty('--accent-fg', ramp.accentFg)
  root.style.setProperty('--accent-text', ramp.accentText)
  root.style.setProperty('--accent-hover', ramp.accentHover)
  root.style.setProperty('--accent-pressed', ramp.accentPressed)
  root.style.setProperty('--accent-muted', ramp.accentMuted)
  root.style.setProperty('--accent-boundary', ramp.accentBoundary)
  root.style.setProperty('--focus-ring', ramp.focusRing)

  // Keep shadcn --primary / --ring in sync so any remaining bg-primary refs match
  const [r, g, b] = hexToRgb(ramp.accent)
  const [fgR, fgG, fgB] = hexToRgb(ramp.accentFg)
  const [h, s, l] = linearToHsl(r, g, b)
  const [fh, fs, fl] = linearToHsl(fgR, fgG, fgB)
  root.style.setProperty('--primary', `${h} ${s}% ${l}%`)
  root.style.setProperty('--primary-foreground', `${fh} ${fs}% ${fl}%`)
  root.style.setProperty('--ring', `${h} ${s}% ${l}%`)
}
